import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  useToast,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { IoMdLock } from "react-icons/io";
import { BsUnlockFill } from "react-icons/bs";
import axios from 'axios';
import Layout from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import Users from "./pages/Users";
import Wallets from "./pages/Wallets";
import Friends from "./pages/Friends";
import Profile from "./pages/Profile";
import CoinEarnings from "./pages/CoinEarnings";
import SelfieApprovals from "./pages/SelfieApprovals"; // Updated from VoiceApproval
import SubscriptionPlans from "./pages/SubscriptionPlans"; // Updated from CoinPackages
import CallTransactions from "./pages/CallTransactions";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import Analytics from "./pages/Analytics";
import AdvancedReports from "./pages/AdvancedReports";
import AdminDashboard from './pages/AdminDashboard';
// import SubscriptionHistory from './pages/SubscriptionHistory'; // Updated from PurchaseHistory
import "react-date-picker/dist/DatePicker.css";
import "react-calendar/dist/Calendar.css";
import Rooms from './pages/Rooms';
import UserCoinDetails from './pages/UserCoinDetails';
import UserCallHistory from './pages/UserCallHistory';
// import SubscriptionHistoryDetails from './pages/SubscriptionHistoryDetails'; // Updated from PurchaseHistoryDetails
import PushNotifications from './pages/PushNotifications';
import InAppNotifications from './pages/InAppNotifications';
import UserActivity from './pages/UserActivity';
import Badges from './pages/Badges';
import CustomRingtone from './pages/CustomRingtone';
import Revenue from './pages/Revenue';
import Banners from './pages/Banners';
import PurchaseHistory from './pages/PurchaseHistory';
import SuperAdminProtected from './components/utilities/SuperAdminProtected';
import Unauthorized from './pages/Unauthorized';
import ContactUs from './pages/ContactUs';
import Chats from './pages/Chats';
import { getUserRole } from './components/utilities/getUserRole';
import Partners from './pages/Partners';
// --- OLD / REMOVED IMPORTS (Commented out) ---
// import VoiceApproval from "./pages/VoiceApproval";
// import Withdrawals from "./pages/Withdrawals";
// import CoinPackages from "./pages/CoinPackages";
// import Gifts from "./pages/Gifts";
// import CallConfig from "./pages/CallConfig";
// import ConversionSettings from "./pages/ConversionSettings";
// import PurchaseHistory from './pages/PurchaseHistory';
// import PurchaseHistoryDetails from './pages/PurchaseHistoryDetails';
// import Coupons from './pages/Coupons';
// import Customisations from './pages/Customisations';
// import Leaderboard from './pages/Leaderboard';
// import Competitions from './pages/Competitions';
// import SpinManager from './pages/SpinManager';
// import MoreOptionsConfiguration from './pages/MoreOptionsConfiguration';
// import DailyCallRewards from './pages/DailyCallRewards';
// import Stories from './pages/Stories';

// API configuration
const API_URL = 'https://dev-admin-spoonland.arrowcoders.in/api';
// const API_URL = "http://localhost:6060";

// Set up axios defaults
axios.defaults.baseURL = API_URL;
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Axios interceptor to add auth token to requests
axios.interceptors.request.use(
  config => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Auth Context
const AuthContext = React.createContext();
export { AuthContext };

const useAuth = () => {
  return React.useContext(AuthContext);
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const storedUser = localStorage.getItem('adminUser');

    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.post('/api/v1/admin/auth/login', { email, password });

      if (response.data.success) {
        const { token, user } = response.data;
        localStorage.setItem('adminToken', token);
        localStorage.setItem('adminUser', JSON.stringify(user));
        setUser(user);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Login error:', err.response?.data || err.message);
      setError(err.response?.data?.message || 'Login failed');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setUser(null);
  };

  const value = {
    user,
    login,
    logout,
    loading,
    error
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Center minH="100vh" bg="#030a1c">
        <Spinner size="xl" color="cyan.400" />
      </Center>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { login, loading, error: authError } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (authError) setError(authError);
  }, [authError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // let tempEmail=''
    // if(email==='admin@spoonland.in'){
    //   tempEmail='admin@cockroach.in'
    // }else{
    //   tempEmail='abc@abc.com'
    // }
    const success = await login(email, password);
    if (success) {
      toast({
        title: "Login successful",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      navigate("/");
    } else {
      toast({
        title: "Login failed",
        description: error || "Invalid email or password",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 sm:p-6"
      style={{
        background:
          "radial-gradient(1200px 800px at 85% -10%, rgba(6, 182, 212, 0.25), transparent 60%)," +
          "radial-gradient(900px 700px at -10% 10%, rgba(37, 99, 235, 0.2), transparent 55%)," +
          "linear-gradient(180deg, #030a1c 0%, #01040f 100%)",
      }}
    >
      <div
        className="w-full max-w-md rounded-3xl overflow-hidden backdrop-blur-2xl"
        style={{
          background: "rgba(4, 18, 48, 0.85)",
          border: "1px solid rgba(56, 189, 248, 0.35)",
          boxShadow: "0 32px 80px rgba(0,0,0,0.8), 0 0 40px rgba(6,182,212,0.2)",
          animation: "modalIn 0.35s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        <div
          className="px-8 pt-10 pb-6 text-center"
          style={{
            borderBottom: "1px solid rgba(56, 189, 248, 0.2)",
            background: "linear-gradient(180deg, rgba(6, 182, 212, 0.1), transparent)",
          }}
        >
          <div className="flex justify-center mb-5">
            <div className="relative">
              <img
                src="/spoonland-logo.jpeg"
                alt="Spoonland"
                className="w-20 h-20 object-cover"
                style={{
                  borderRadius: "24px",
                  border: "1.5px solid rgba(34, 211, 238, 0.6)",
                  boxShadow: "0 0 30px rgba(6,182,212,0.45)",
                }}
              />
              <span
                className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2"
                style={{
                  background: "#22d3ee",
                  borderColor: "#030a1c",
                  boxShadow: "0 0 10px #22d3ee",
                }}
              />
            </div>
          </div>
          <h1
            className="font-extrabold tracking-wider mb-1 text-2xl"
            style={{
              background: "linear-gradient(135deg, #22d3ee 0%, #3b82f6 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Spoonland
          </h1>
          <p
            className="text-xs uppercase tracking-[0.2em] font-semibold text-cyan-300"
          >
            Admin Panel
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-8 py-7 space-y-5">
          {error && (
            <div
              className="rounded-xl px-4 py-3 text-sm"
              style={{
                background: "rgba(244, 63, 94, 0.15)",
                border: "1px solid rgba(244, 63, 94, 0.35)",
                color: "#fda4af",
              }}
            >
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-cyan-200 mb-1.5">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email"
              required
              className="w-full bg-cyan-950/60 text-cyan-50 text-sm rounded-xl px-4 py-3 border border-cyan-400/30 focus:outline-none focus:border-cyan-300 backdrop-blur-md shadow-inner transition"
              autoComplete="email"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-cyan-200 mb-1.5">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                className="w-full bg-cyan-950/60 text-cyan-50 text-sm rounded-xl px-4 py-3 pr-11 border border-cyan-400/30 focus:outline-none focus:border-cyan-300 backdrop-blur-md shadow-inner transition"
                autoComplete="current-password"
              />
              <span
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 cursor-pointer select-none text-cyan-400 hover:text-cyan-200"
              >
                {showPassword ? (
                  <BsUnlockFill className="text-xl" />
                ) : (
                  <IoMdLock className="text-xl" />
                )}
              </span>
            </div>
          </div>

          <button
            type="submit"
            className="w-full py-3.5 rounded-xl font-bold text-white transition shadow-lg text-sm tracking-wide"
            disabled={loading}
            style={{
              background: "linear-gradient(135deg, #06b6d4 0%, #2563eb 100%)",
              boxShadow: "0 8px 25px rgba(6, 182, 212, 0.45)",
              opacity: loading ? 0.75 : 1,
            }}
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>

          <p className="text-center text-xs pt-2 text-cyan-300/50 font-medium">
            Protected access · Authorized personnel only
          </p>
        </form>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  return (
    <div className="min-h-screen" style={{ background: "transparent" }}>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/users" element={<Users />} />
                      <Route path="/partners" element={<Partners />} />
                      <Route path="/coin-earnings" element={<CoinEarnings />} />
                      <Route path="/selfie-approvals" element={<SelfieApprovals />} />
                      <Route path="/subscription-plans" element={<SubscriptionPlans />} />
                      <Route path="/call-transactions" element={<CallTransactions />} />
                      <Route path="/analytics" element={<Analytics />} />
                      <Route path="/reports" element={<Reports />} />
                      <Route path="/advanced-reports" element={<AdvancedReports />} />
                      <Route path="/settings" element={<SuperAdminProtected><Settings /></SuperAdminProtected>} />
                      <Route path="/wallets" element={<Wallets />} />
                      <Route path="/friends" element={<Friends />} />
                      <Route path="/profile" element={<Profile />} />
                      {/* <Route path="/subscription-history" element={<SubscriptionHistory />} /> */}
                      <Route path="/chats" element={
                        getUserRole()?.email === "admin@arrowcoders.com" 
                          ? <Chats /> 
                          : <Navigate to="/unauthorized" replace />
                      } />
                      <Route path="/admin-managment" element={<SuperAdminProtected><AdminDashboard /></SuperAdminProtected>} />
                      <Route path="/Room-Managment" element={<Rooms />} />
                      <Route path="/call-transactions/:userId" element={<UserCallHistory />} />
                      {/* <Route path="/subscription-history/:userId" element={<SubscriptionHistoryDetails />} /> */}
                      <Route path="/unauthorized" element={<Unauthorized />} />
                      <Route path="/coin-earnings/user/:id" element={<UserCoinDetails />} />
                      <Route path="/push-notifications" element={<PushNotifications />} />
                      <Route path="/in-app-notifications" element={<InAppNotifications />} />
                      <Route path="/user-activity" element={<UserActivity />} />
                      <Route path="/badges" element={<Badges />} />
                      <Route path="/revenue" element={<Revenue />} />
                      <Route path="/banners" element={<Banners />} />
                      <Route path="/purchase-history" element={<PurchaseHistory />} />
                      <Route path="/contact-us" element={<ContactUs />} />

                      {/* --- COMMENTED OUT OLD / REMOVED ROUTES --- */}
                      {/* <Route path="/voice-approval" element={<VoiceApproval />} /> */}
                      {/* <Route path="/withdrawals" element={<Withdrawals />} /> */}
                      {/* <Route path="/coin-packages" element={<CoinPackages />} /> */}
                      {/* <Route path="/conversion-settings" element={<SuperAdminProtected><ConversionSettings /></SuperAdminProtected>} /> */}
                      {/* <Route path="/gifts" element={<Gifts />} /> */}
                      {/* <Route path="/call-config" element={<SuperAdminProtected><CallConfig /></SuperAdminProtected>} /> */}
                      {/* <Route path="/purchase-history" element={<PurchaseHistory />} /> */}
                      {/* <Route path="/purchase-history/:userId" element={<PurchaseHistoryDetails />} /> */}
                      {/* <Route path="/coupons" element={<Coupons />} /> */}
                      {/* <Route path="/leaderboard" element={<Leaderboard />} /> */}
                      {/* <Route path="/leaderboard/competitions" element={<Competitions />} /> */}
                      {/* <Route path="/customisations" element={<SuperAdminProtected><Customisations /></SuperAdminProtected>} /> */}
                      {/* <Route path="/spin-manager" element={<SuperAdminProtected><SpinManager /></SuperAdminProtected>} /> */}
                      {/* <Route path="/more-options" element={<MoreOptionsConfiguration />} /> */}
                      {/* <Route path="/daily-call-rewards" element={<DailyCallRewards />} /> */}
                      {/* <Route path="/stories" element={<Stories />} /> */}

                    </Routes>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Router>
      </AuthProvider>
    </div>
  );
}

export default App;